import { Link, useLocation } from "react-router-dom";
import { useState } from "react";
import { Menu, X, Mail, MessageCircle, ArrowLeft, ShieldCheck } from "lucide-react";
import Logo from "@/components/Logo";
import Seo from "@/components/Seo";
import { Button } from "@/components/kit";
import { COMPANY, LEGAL_NAV } from "@/lib/legal";
import SiteFooter from "@/components/SiteFooter";

export default function LegalLayout({ seoTitle, seoDesc, path, title, subtitle, updated, toc = [], jsonLd, children }) {
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  return (
    <div className="bg-white text-slate-900 min-h-screen flex flex-col">
      <Seo title={seoTitle} description={seoDesc} path={path} jsonLd={jsonLd} />

      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo to="/" iconSize={32} />
          <nav className="hidden md:flex items-center gap-7 text-sm font-medium text-slate-600">
            <Link to="/" className="hover:text-sky-600">Beranda</Link>
            {LEGAL_NAV.map((l) => (
              <Link key={l.to} to={l.to} className={pathname === l.to ? "text-sky-600" : "hover:text-sky-600"}>
                {l.label}
              </Link>
            ))}
          </nav>
          <Link to="/register" className="hidden md:block">
            <Button size="sm">Daftar Gratis</Button>
          </Link>
          <button className="md:hidden p-2 -mr-2 text-slate-700" onClick={() => setMenuOpen((v) => !v)} aria-label="Menu" data-testid="legal-menu-btn">
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
        {menuOpen && (
          <div className="md:hidden border-t border-slate-100 bg-white/95 backdrop-blur">
            <nav className="px-6 py-3 flex flex-col text-sm font-medium text-slate-700">
              <Link to="/" onClick={() => setMenuOpen(false)} className="py-2.5 hover:text-sky-600">Beranda</Link>
              {LEGAL_NAV.map((l) => (
                <Link key={l.to} to={l.to} onClick={() => setMenuOpen(false)} className={`py-2.5 ${pathname === l.to ? "text-sky-600" : "hover:text-sky-600"}`}>{l.label}</Link>
              ))}
            </nav>
          </div>
        )}
      </header>

      {/* Hero */}
      <section className="relative border-b border-slate-100">
        <div className="absolute inset-0 grid-bg opacity-40" />
        <div className="max-w-4xl mx-auto px-6 py-14 relative">
          <span className="inline-flex items-center gap-2 text-xs font-semibold text-sky-700 bg-sky-50 border border-sky-100 rounded-full px-3 py-1">
            <ShieldCheck className="w-3.5 h-3.5" /> Dokumen Legal {COMPANY.brand}
          </span>
          <h1 className="font-display text-3xl md:text-4xl font-extrabold mt-4">{title}</h1>
          {subtitle && <p className="text-slate-600 mt-3 max-w-2xl">{subtitle}</p>}
          <p className="text-sm text-slate-400 mt-4">Terakhir diperbarui: {updated || COMPANY.updated}</p>
        </div>
      </section>

      {/* Body */}
      <main className="flex-1">
        <div className={`max-w-5xl mx-auto px-6 py-12 ${toc.length > 0 ? "grid lg:grid-cols-[220px_1fr] gap-10" : ""}`}>
          {toc.length > 0 && (
            <aside className="hidden lg:block">
              <div className="sticky top-24">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-400 mb-3">Daftar Isi</p>
                <nav className="space-y-1.5 text-sm">
                  {toc.map((t) => (
                    <a key={t.id} href={`#${t.id}`} className="block text-slate-500 hover:text-sky-600 transition leading-snug">{t.label}</a>
                  ))}
                </nav>
              </div>
            </aside>
          )}
          <article className="legal-prose max-w-none">{children}</article>
        </div>
      </main>

      {/* Contact strip */}
      <section className="bg-slate-50 border-t border-slate-100">
        <div className="max-w-5xl mx-auto px-6 py-10">
          <h3 className="font-display text-xl font-bold">Butuh bantuan?</h3>
          <p className="text-slate-600 mt-2 text-sm">Hubungi tim {COMPANY.brand} untuk pertanyaan terkait dokumen ini, pembayaran, atau pengembalian dana.</p>
          <div className="flex flex-wrap gap-3 mt-4">
            <a href={`mailto:${COMPANY.email}`} className="inline-flex items-center gap-2 text-sm font-semibold text-sky-700 bg-white border border-sky-200 rounded-xl px-4 py-2 hover:bg-sky-50 transition">
              <Mail className="w-4 h-4" /> {COMPANY.email}
            </a>
            <a href={`https://wa.me/${COMPANY.whatsapp}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 text-sm font-semibold text-emerald-700 bg-white border border-emerald-200 rounded-xl px-4 py-2 hover:bg-emerald-50 transition">
              <MessageCircle className="w-4 h-4" /> WhatsApp {COMPANY.whatsappDisplay}
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <SiteFooter />
    </div>
  );
}

// Small helpers for consistent section styling
export function Section({ id, title, children }) {
  return (
    <section id={id} className="scroll-mt-24 mb-9">
      <h2 className="font-display text-xl font-bold text-slate-900 mb-3">{title}</h2>
      <div className="space-y-3 text-[15px] leading-relaxed text-slate-600">{children}</div>
    </section>
  );
}
